import { useEffect, useState } from "react";
import { Copy, Download, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, slDate, downloadBlob } from "@/lib/api";
import { BankDetails } from "@/components/manage/PaymentPanel";

const TEMPLATES = { minimal: ["Minimalistična", "#FFFFFF", "#231F20", "#C5A880"], romanticna: ["Romantična", "#FBF3F0", "#5A3E3B", "#C99A8E"], sampanjec: ["Šampanjec", "#F5EFE6", "#231F20", "#B39264"], crno_belo: ["Moderna črno-bela", "#111111", "#FFFFFF", "#C5A880"] };
const MESSAGES = ["Ujemi trenutek. Deli spomin.", "Skeniraj QR kodo in deli svoje fotografije z nama!", "Pomagaj nama ujeti najlepše trenutke najinega dne!"];

export default function QrPanel({ wedding, onSaved }) {
  const [tpl, setTpl] = useState(wedding.qr_template || "minimal");
  const [msg, setMsg] = useState(wedding.qr_message || MESSAGES[0]);
  const [, bg, fg, accent] = TEMPLATES[tpl];
  const [preview, setPreview] = useState(null);
  const [billing, setBilling] = useState(null);
  const paid = wedding.payment_status === "placano" || wedding.is_demo || wedding.viewer_role === "admin";
  const loadBilling = () => api.get(`/manage/weddings/${wedding.id}/billing`).then((r) => setBilling(r.data));
  useEffect(() => {
    api.get(`/manage/weddings/${wedding.id}/qr`, { params: { format: "preview" }, responseType: "blob" }).then((r) => setPreview(URL.createObjectURL(r.data)));
    loadBilling();
  }, [wedding.id]);
  const entitled = billing?.qr_designs?.entitled || wedding.viewer_role === "admin";
  const unlock = async () => {
    try { const r = await api.post(`/manage/weddings/${wedding.id}/pay`, { type: "qr_designs" }); setBilling(r.data); toast.success("Podatki za plačilo so pripravljeni."); } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async (body) => {
    try { const r = await api.patch(`/manage/weddings/${wedding.id}`, body); onSaved(r.data); } catch (e) { toast.error(errMsg(e)); }
  };
  const dl = async (format) => {
    try { await downloadBlob(`/manage/weddings/${wedding.id}/qr?format=${format}&template=${tpl}`, `qr_${wedding.slug}${format === "design_svg" ? `_${tpl}_dizajn.svg` : format === "pdf" ? `_${tpl}.pdf` : `.${format}`}`); } catch (e) { toast.error("Prenos ni dovoljen ali ni uspel."); }
  };
  const copy = () => { navigator.clipboard.writeText(wedding.guest_url); toast.success("Povezava je kopirana."); };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="qr-panel">
      <div className="flex justify-center">
        <div className="w-full max-w-sm aspect-[148/210] rounded-2xl p-8 flex flex-col items-center justify-between text-center shadow-[0_20px_60px_rgba(35,31,32,0.12)] border" style={{ background: bg, color: fg, borderColor: accent }} data-testid="qr-template-preview">
          <div><p className="font-display italic text-3xl">{wedding.partner1} & {wedding.partner2}</p><p className="text-[10px] tracking-[0.3em] uppercase mt-2" style={{ color: accent }}>{slDate(wedding.wedding_date)}</p></div>
          {preview && <img src={preview} alt="QR koda – predogled" className="w-3/4 rounded-lg" style={{ filter: tpl === "crno_belo" ? "invert(1)" : "none" }} data-testid="qr-preview-image" />}
          <p className="font-display text-lg leading-snug">{msg}</p>
        </div>
      </div>
      <div className="space-y-6">
        <div className="card-lux p-6 space-y-4">
          <h3 className="font-display text-2xl">Predloga za tisk</h3>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(TEMPLATES).map(([k, [l, b, , a]]) => (
              <button key={k} onClick={() => { setTpl(k); save({ qr_template: k }); }} className={`p-3 rounded-xl border text-left text-sm flex items-center gap-2 ${tpl === k ? "border-[#231F20]" : "border-[#DFD5C5]"}`} data-testid={`qr-template-${k}`}>
                <span className="w-5 h-5 rounded-full border" style={{ background: b, borderColor: a }} />{l}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            {MESSAGES.map((m) => (
              <label key={m} className="flex gap-2 text-sm items-start cursor-pointer">
                <input type="radio" checked={msg === m} onChange={() => { setMsg(m); save({ qr_message: m }); }} className="mt-1 accent-[#231F20]" data-testid="qr-message-option" />{m}
              </label>
            ))}
          </div>
        </div>
        <div className="card-lux p-6 space-y-4 border-2 border-[#C5A880]" data-testid="qr-designs-upgrade">
          <h3 className="font-display text-2xl">{entitled ? "Pripravljeni QR dizajni" : `Prenesi pripravljene QR dizajne – doplačilo ${billing?.qr_designs?.price ?? 20} €`}</h3>
          {entitled ? (
            <>
              <p className="text-sm text-[#6C6560]">Prenos je odklenjen. Izberite predlogo zgoraj in prenesite datoteko za tisk.</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => dl("pdf")} className="btn-dark px-5 py-2.5 text-sm flex items-center gap-2" data-testid="couple-qr-download-pdf"><Download size={15} /> PDF za tisk (A5)</button>
                <button onClick={() => dl("design_svg")} className="btn-line px-5 py-2.5 text-sm" data-testid="couple-qr-download-design-svg">SVG dizajn (vektorsko)</button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-[#6C6560]">Želita uporabiti naše že pripravljene QR dizajne? Za enkratno doplačilo {billing?.qr_designs?.price ?? 20} € lahko preneseta pripravljene datoteke in jih natisneta sama ali predata izbranemu tiskarju.</p>
              {!billing?.qr_designs?.open_order && <button onClick={unlock} className="btn-dark px-6 h-12 text-sm flex items-center gap-2" data-testid="qr-unlock-button"><Lock size={15} /> Odkleni prenos – {billing?.qr_designs?.price ?? 20} €</button>}
              {billing?.qr_designs?.open_order && <BankDetails bank={billing.bank} order={billing.qr_designs.open_order} />}
            </>
          )}
        </div>
        <div className="card-lux p-6 space-y-4">
          <h3 className="font-display text-2xl">QR koda za deljenje</h3>
          {paid ? (
            <div className="flex flex-wrap gap-2">
              <button onClick={() => dl("png")} className="btn-dark px-5 py-2.5 text-sm flex items-center gap-2" data-testid="couple-qr-download-png"><Download size={15} /> PNG</button>
              <button onClick={() => dl("svg")} className="btn-line px-5 py-2.5 text-sm" data-testid="couple-qr-download-svg">SVG</button>
            </div>
          ) : <p className="text-sm text-[#8A5A2B] bg-[#FBEBDD] rounded-xl p-3" data-testid="qr-locked-note">Osnovna QR koda za deljenje galerije bo na voljo za prenos po aktivaciji galerije.</p>}
          <div className="flex items-center gap-2 bg-[#F3ECE1] rounded-xl p-3">
            <code className="text-xs flex-1 truncate" data-testid="qr-guest-url">{wedding.guest_url}</code>
            <button onClick={copy} className="p-2 rounded-lg hover:bg-white" data-testid="qr-copy-link" aria-label="Kopiraj"><Copy size={16} /></button>
          </div>
          <p className="text-xs text-[#6C6560]">QR koda je veljavna ves čas hrambe galerije. Povezava je nenapovedljiva in poroka ni javno iskalna.</p>
        </div>
      </div>
    </div>
  );
}
